import React from 'react'
import { FormattedMessage } from 'react-intl'

import { Outcome } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'
import { outcomeChartConstants } from '@lyrahealth-inc/ui-core/lib/constants/outcomeConstants'
import { OutcomeChart, OutcomeChartResponse } from '@lyrahealth-inc/ui-core/lib/molecules/outcomeChart/OutcomeChart'

import styles from './clientDetails.module.scss'
import { track } from '../../../../mixpanel/mixpanelTracking'

type OutcomeHistoryChartsProps = {
  outcomes: Outcome[]
  clientName: string
  answerModalAction?: (response: OutcomeHistoryChartResponse) => void
  headingLevel?: 2 | 3
  /** Hides the "Outcomes History" heading, e.g. in the DA clinical assessment history experience. */
  showHeading?: boolean
}

export type OutcomeHistoryChartResponse = Omit<OutcomeChartResponse, 'title'> & { title: string }

/**
 * Anchor for scrolling to a specific chart. Lives here rather than on OutcomeChart itself so the
 * "Go to chart" row action in CurrentMeasuresTable has a stable target without ui-core needing to
 * know anything about it.
 */
export const getChartAnchorId = (assessmentType: string) => `OutcomeHistoryCharts-chart-${assessmentType}`

export const OutcomeHistoryCharts: React.FC<OutcomeHistoryChartsProps> = ({
  outcomes,
  clientName,
  answerModalAction,
  headingLevel = 2,
  showHeading = true,
}) => {
  const submittedOutcomes = outcomes.filter((outcome) => outcome.response_date)

  if (submittedOutcomes.length === 0) return null

  const normalizedAnswerModalAction = answerModalAction
    ? (response: OutcomeChartResponse) =>
        answerModalAction({
          ...response,
          title: response.title ?? response.assessmentType,
        })
    : undefined
  const heading = (
    <FormattedMessage
      defaultMessage='Outcomes History'
      description='Heading for the client assessment outcome charts'
    />
  )

  return (
    <div data-test-id='ClientOutcomes-charts'>
      {showHeading &&
        (headingLevel === 3 ? (
          <h3 className={styles['history-title']}>{heading}</h3>
        ) : (
          <h2 className={styles['history-title']}>{heading}</h2>
        ))}
      <div className={styles['charts-container']}>
        {outcomeChartConstants.map((assessment) => (
          <OutcomeChart
            key={assessment.name}
            assessmentType={assessment.name}
            chartAnchorId={getChartAnchorId(assessment.name)}
            outcomesData={submittedOutcomes}
            answerModalAction={normalizedAnswerModalAction}
            clientName={clientName}
            track={track}
            isProviderPortal
          />
        ))}
      </div>
    </div>
  )
}
